import {
  Box,
  TextField,
  useTheme,
  Switch,
  FormControlLabel,
  Grid,
  CircularProgress,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import Buttons from 'components/elements/Buttons';
import { strings } from 'i18n/Strings';
import { debounce } from 'lodash';
import { useDispatch } from 'react-redux';
import { register } from 'service/UserService';
import { setSnackBarFailed, setSnackBarSuccess } from 'store/snackBar';
import { type RegisterData } from 'types';
import { colorTheme } from 'theme';
import { commonConstantTexts, roleIds, roles } from 'constants/constantText';
import { pagesConstantList } from 'routes/Constant';

interface IUser {
  firstName?: string;
  lastName?: string;
  email?: string;
  empId?: string;
}

export interface ICreateUserForm {
  onClose: () => void;
  updateEmployeeData: () => void;
}

const CreateUser = (props: ICreateUserForm): JSX.Element => {
  const { onClose, updateEmployeeData } = props;
  const dispatch = useDispatch();
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState(roles.MMEmployeeRole);
  const [formError, setFormError] = useState<IUser>({});
  const inputRef = useRef<HTMLInputElement>(null);
  const [loader, setLoader] = useState(false);

  /**
   * there is a bug in mui in which autofocus sometimes doesn't
   * happen in react strict mode
   * https://github.com/mui/material-ui/issues/33004
   */

  useEffect(() => {
    if (inputRef.current != null) {
      inputRef.current.focus();
    }
  }, []);

  const handleInputChange = (
    type: string,
    e: { target: { value: string } },
  ): undefined | string => {
    switch (type) {
      case commonConstantTexts.firstName: {
        setFirstName(e.target.value);
        return;
      }
      case commonConstantTexts.lastName: {
        setLastName(e.target.value);
        return;
      }
      case commonConstantTexts.email: {
        setEmail(e.target.value);
        return;
      }
      default:
        return '';
    }
  };

  const handleValidation = (
    type: string,
    e: { target: { value: string } },
  ): void => {
    const err: IUser = {};
    switch (type) {
      case commonConstantTexts.firstName: {
        if (e.target.value === '') {
          err.firstName = strings.displayText.firstNameRequiredText;
        }
        setFormError({ ...formError, firstName: err.firstName });
        break;
      }
      case commonConstantTexts.lastName: {
        if (e.target.value === '') {
          err.lastName = strings.displayText.lastNameRequiredText;
        }
        setFormError({ ...formError, lastName: err.lastName });
        break;
      }
      case commonConstantTexts.email: {
        const emailRegex =
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (e.target.value === '') {
          err.email = strings.displayText.emailEmptyText;
        } else if (!emailRegex.test(e.target.value)) {
          err.email = strings.displayText.invalidEmailText;
        }
        setFormError({ ...formError, email: err.email });
        break;
      }
      default:
        break;
    }
  };

  const debounceHandler = useCallback(
    debounce((type: string, e: { target: { value: string } }) => {
      handleValidation(type, e);
    }, 600),
    [],
  );

  const validateForm = (): boolean => {
    const err: IUser = {};
    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z-0-9]+\.)+[a-zA-Z]{2,}))$/;

    if (email === '') err.email = strings.displayText.emailEmptyText;
    if (!emailRegex.test(email)) {
      err.email = strings.displayText.invalidEmailText;
    }
    if (firstName === '') {
      err.firstName = strings.displayText.firstNameRequiredText;
    }
    if (lastName === '')
      err.lastName = strings.displayText.lastNameRequiredText;

    setFormError({ ...err });
    return Object.keys(err).length < 1;
  };

  const handleRoleChange = (selectedRole: string): void => {
    setRole(selectedRole);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const formFirstName = data.get('firstName')?.toString() ?? '';
    const formLastName = data.get('lastName')?.toString() ?? '';
    const formEmail = data.get('email')?.toString() ?? '';
    const roleId =
      role === pagesConstantList.ceo
        ? roleIds.CeoRoleId
        : role === pagesConstantList.auditor
        ? roleIds.AuditorRole
        : roleIds.MMEmployeeRoleId;
    const value: RegisterData = {
      firstName: formFirstName,
      lastName: formLastName,
      email: formEmail,
      role,
      roleId: parseInt(roleId),
    };

    const isValid = validateForm();

    if (isValid) {
      void (async () => {
        // create user service here
        setLoader(true);
        register(value)
          .then(() => {
            dispatch(
              setSnackBarSuccess({
                snackBarMessage: strings.displayText.userCreatedSuccessText,
              }),
            );
            updateEmployeeData();
            setLoader(false);
            onClose();
          })
          .catch((err) => {
            dispatch(
              setSnackBarFailed({ snackBarMessage: err.response.data.msg }),
            );
            setLoader(false);
            console.error(`Something went wrong: ${String(err)}`);
          });
      })();
    }
  };

  const isDisabled =
    formError?.email !== undefined ||
    formError?.firstName !== undefined ||
    formError?.lastName !== undefined ||
    loader;

  return (
    <Box sx={{ maxWidth: '30rem' }}>
      <Box
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit}
      >
        <TextField
          inputRef={inputRef}
          margin="normal"
          required
          fullWidth
          label={strings.displayText.firstName}
          name={commonConstantTexts.firstName}
          onChange={(e: {
            target: {
              name: string;
              value: string;
            };
          }) => {
            handleInputChange(commonConstantTexts.firstName, e);
            debounceHandler(commonConstantTexts.firstName, e);
          }}
          sx={{
            '& p': {
              color: 'red',
            },
            mb: 2,
          }}
          helperText={formError?.firstName}
        />

        <TextField
          margin="normal"
          required
          fullWidth
          label={strings.displayText.lastName}
          name={commonConstantTexts.lastName}
          onChange={(e) => {
            handleInputChange(commonConstantTexts.lastName, e);
            debounceHandler(commonConstantTexts.lastName, e);
          }}
          sx={{
            '& p': {
              color: 'red',
            },
            mb: 2,
          }}
          helperText={formError?.lastName}
        />

        <TextField
          margin="normal"
          required
          fullWidth
          label={strings.displayText.emailAddress}
          name={commonConstantTexts.email}
          autoComplete="email"
          onChange={(e: {
            target: {
              name: string;
              value: string;
            };
          }) => {
            handleInputChange(commonConstantTexts.email, e);
            debounceHandler(commonConstantTexts.email, e);
          }}
          sx={{
            '& p': {
              color: 'red',
            },
            mb: 2,
            input: {
              '&:-webkit-autofill': {
                '-webkit-box-shadow': '0 0 0 100px #eef9fa inset',
                '-webkit-text-fill-color': colors.greenAccent[100],
              },
            },
          }}
          helperText={formError?.email}
        />
        <Grid container spacing={3}>
          <Grid item xs={6} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={role === roles.CeoRole}
                  onChange={() => {
                    handleRoleChange(
                      role !== roles.CeoRole
                        ? roles.CeoRole
                        : roles.MMEmployeeRole,
                    );
                  }}
                />
              }
              label={strings.displayText.approver}
            />
          </Grid>
          {/* <Grid item xs={6} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={role === roles.AuditorRole}
                  onChange={() => {
                    handleRoleChange(
                      role !== roles.AuditorRole
                        ? roles.AuditorRole
                        : roles.MMEmployeeRole,
                    );
                  }}
                />
              }
              label={strings.displayText.auditor}
            />
          </Grid> */}
        </Grid>
        <Buttons
          disabled={isDisabled}
          style={{ py: '0.8rem', mt: '1rem' }}
          type="submit"
          fullWidth={true}
          endIcon={loader ? <CircularProgress size={15} /> : null}
        >
          {strings.displayText.saveText}
        </Buttons>
      </Box>
    </Box>
  );
};

export default CreateUser;
